<script>
  // Expandable row for one SFTP capture: an upload (filename/size/text body)
  // or an auth attempt (username/method/key/success/reason). Mirrors
  // CaptureRow's interaction model.
  import { api } from '../lib/api.js';
  import { ui } from '../lib/ui.svelte.js';

  let { capture, isAdmin = false, selected = false, index = 0, onToggle = null, onDeleted = () => {} } = $props();

  let open = $state(false);
  function toggle() { open = !open; }
  function fmt(t) { return t ? new Date(t).toLocaleString() : '—'; }
  function fmtBytes(n) {
    if (n == null) return '—';
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(1)} MB`;
  }

  const isUpload = $derived(capture.kind === 'upload');

  // The bare source IP (strip the :port; handle [v6]:port too).
  function stripPort(addr) {
    if (!addr) return '';
    if (addr.startsWith('[')) return addr.replace(/^\[/, '').replace(/\]:\d+$/, '').replace(/\]$/, '');
    return addr.replace(/:\d+$/, '');
  }
  const remoteIP = $derived(stripPort(capture.remote_addr));
  let blocked = $state(false);

  async function blockThisIp(e) {
    e?.stopPropagation();
    if (!remoteIP) return;
    const ok = await ui.ask({
      title: 'Block this IP forever?',
      message: `${remoteIP} will be permanently blocked from the SFTP server — its connections are dropped before the SSH handshake. You can unblock it later from the Firewall tab.`,
      confirmText: 'Block IP', destructive: true,
    });
    if (!ok) return;
    try {
      await api.blockSftpIp(remoteIP, 'manual (from activity)');
      blocked = true;
      ui.snack(`Blocked ${remoteIP}`, 'success');
    } catch (err) { ui.snack(err.message, 'error'); }
  }

  async function copyRow(e) {
    e?.stopPropagation();
    try { await navigator.clipboard.writeText(JSON.stringify(capture, null, 2)); ui.snack('Copied', 'success', 1500); }
    catch (err) { ui.snack('Copy failed: ' + err.message, 'error'); }
  }
  async function del(e) {
    e?.stopPropagation();
    const ok = await ui.ask({ title: 'Delete entry?', message: 'This capture will be permanently removed.', confirmText: 'Delete', destructive: true });
    if (!ok) return;
    try { await api.deleteSftpCapture(capture.id); ui.snack('Deleted', 'success'); onDeleted(capture.id); }
    catch (err) { ui.snack(err.message, 'error'); }
  }
</script>

<article class:row-selected={selected}>
  <div class="row">
    {#if onToggle}
      <label class="row-check">
        <input type="checkbox" checked={selected} onclick={(e) => onToggle(index, e.shiftKey)} aria-label="Select capture" />
      </label>
    {/if}
    <button type="button" class="row-main" onclick={toggle} aria-expanded={open}>
      {#if isUpload}
        <span class="chip chip-upload">upload</span>
      {:else}
        <span class="chip {capture.success ? 'chip-ok' : 'chip-fail'}">{capture.success ? 'auth ✓' : 'auth ✗'}</span>
      {/if}
      <div class="flex-1 min-w-0">
        <div class="row-title">
          {#if isUpload}
            {capture.path || capture.filename || '/'}
          {:else}
            {capture.username_tried || '(no username)'} · {capture.method}
          {/if}
        </div>
        <div class="row-meta">
          <span>{fmt(capture.created_at)}</span>
          {#if isUpload}
            <span>{fmtBytes(capture.size)}</span>
            <span class="font-mono">{capture.is_text ? 'text' : 'binary'}</span>
          {:else}
            {#if capture.key_type}<span class="font-mono">{capture.key_type}</span>{/if}
            {#if capture.reason}<span>{capture.reason}</span>{/if}
          {/if}
          {#if capture.bucket_id}<span>bucket: <span class="font-mono">{capture.bucket_id}</span></span>{:else}<span class="font-mono">unmatched</span>{/if}
          {#if capture.remote_addr}<span>from: <span class="font-mono">{capture.remote_addr}</span></span>{/if}
        </div>
      </div>
      <span class="material-symbols-outlined row-chevron" style:transform={open ? 'rotate(180deg)' : ''}>expand_more</span>
    </button>
    <button type="button" class="row-icon" onclick={copyRow} title="Copy JSON" aria-label="Copy JSON"><span class="material-symbols-outlined text-base">content_copy</span></button>
    {#if isAdmin}
      <button type="button" class="row-icon row-icon-danger" onclick={del} title="Delete" aria-label="Delete"><span class="material-symbols-outlined text-base">delete</span></button>
    {/if}
  </div>

  {#if open}
    <div class="row-body">
      <dl class="kv">
        {#if remoteIP}
          <div>
            <dt>Source IP</dt>
            <dd class="font-mono">
              {#if isAdmin && !blocked}
                <button type="button" class="ip-block" onclick={blockThisIp} title="Block this IP forever">
                  {remoteIP}
                  <span class="material-symbols-outlined ip-block-icon">block</span>
                </button>
              {:else}
                {remoteIP}{#if blocked}<span class="ip-blocked">blocked</span>{/if}
              {/if}
            </dd>
          </div>
        {/if}
        {#if isUpload}
          <div><dt>Path</dt><dd class="font-mono">{capture.path || '—'}</dd></div>
          <div><dt>Filename</dt><dd>{capture.filename || '—'}</dd></div>
          <div><dt>Size</dt><dd>{fmtBytes(capture.size)}{capture.content_truncated ? ' (content truncated)' : ''}</dd></div>
          <div><dt>Type</dt><dd>{capture.is_text ? 'text' : 'binary (content not stored)'}</dd></div>
          {#if capture.modtime}<div><dt>Modified</dt><dd>{fmt(capture.modtime)}</dd></div>{/if}
        {:else}
          <div><dt>Username</dt><dd class="font-mono">{capture.username_tried || '—'}</dd></div>
          <div><dt>Method</dt><dd>{capture.method}</dd></div>
          <div><dt>Result</dt><dd>{capture.success ? 'accepted' : 'rejected'}{capture.reason ? ` — ${capture.reason}` : ''}</dd></div>
          {#if capture.key_type}<div><dt>Key type</dt><dd class="font-mono">{capture.key_type}</dd></div>{/if}
          {#if capture.key_fingerprint}<div><dt>Fingerprint</dt><dd class="font-mono break-all">{capture.key_fingerprint}</dd></div>{/if}
          {#if capture.password_tried}<div><dt>Password tried</dt><dd class="font-mono">{capture.password_tried}</dd></div>{/if}
        {/if}
        {#if capture.ssh_client_version}<div><dt>Client</dt><dd class="font-mono break-all">{capture.ssh_client_version}</dd></div>{/if}
      </dl>
      {#if isUpload && capture.is_text}
        <div class="kv-section">Content{capture.content_truncated ? ' (truncated)' : ''}</div>
        <pre class="body-pre">{capture.content || ''}</pre>
      {/if}
    </div>
  {/if}
</article>

<style>
  .row { display: flex; align-items: stretch; gap: 0; }
  .row-check { display: flex; align-items: center; padding-inline: 0.75rem 0.25rem; cursor: pointer; }
  .row-check input { inline-size: 16px; block-size: 16px; accent-color: rgb(var(--md-sys-color-primary)); cursor: pointer; }
  article.row-selected { background: rgb(var(--md-sys-color-primary) / 0.08); }
  .row-main { flex: 1; min-inline-size: 0; display: flex; align-items: flex-start; gap: 0.75rem; padding: 0.75rem 0.5rem 0.75rem 0.875rem; background: transparent; color: inherit; border: 0; text-align: start; cursor: pointer; }
  .row-main:hover { background: rgb(var(--md-sys-color-on-surface) / 0.04); }
  .row-chevron { align-self: center; color: rgb(var(--md-sys-color-on-surface-variant)); transition: transform 150ms; }
  .row-title { font-family: 'Menlo','Consolas',monospace; font-size: 13px; word-break: break-all; }
  .row-meta { font-size: 12px; color: rgb(var(--md-sys-color-on-surface-variant)); margin-top: 2px; display: flex; flex-wrap: wrap; gap: 0 0.75rem; }
  .chip { padding: 2px 8px; border-radius: 6px; font-size: 11px; font-weight: 500; flex-shrink: 0; }
  .chip-upload { background: rgb(var(--md-sys-color-primary) / 0.15); color: rgb(var(--md-sys-color-primary)); }
  .chip-ok { background: rgb(16 185 129 / 0.18); color: rgb(4 120 87); }
  .chip-fail { background: rgb(var(--md-sys-color-error) / 0.15); color: rgb(var(--md-sys-color-error)); }
  :global([data-theme='dark']) .chip-ok { color: rgb(110 231 183); }
  .row-icon { display: flex; align-items: center; justify-content: center; inline-size: 40px; background: transparent; color: rgb(var(--md-sys-color-on-surface-variant)); border: 0; cursor: pointer; }
  .row-icon:hover { background: rgb(var(--md-sys-color-on-surface) / 0.06); color: rgb(var(--md-sys-color-primary)); }
  .row-icon-danger:hover { color: rgb(var(--md-sys-color-error)); }
  .row-body { padding: 0 0.875rem 0.875rem; display: flex; flex-direction: column; gap: 0.5rem; }
  .kv { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 0.25rem 1rem; margin: 0; font-size: 12px; }
  .kv div { display: grid; grid-template-columns: 110px 1fr; gap: 0.5rem; align-items: baseline; }
  .kv dt { color: rgb(var(--md-sys-color-on-surface-variant)); }
  .kv dd { margin: 0; word-break: break-all; }
  .ip-block { display: inline-flex; align-items: center; gap: 4px; padding: 1px 6px; border: 1px solid rgb(var(--md-sys-color-outline-variant)); border-radius: 6px; background: transparent; color: inherit; font: inherit; cursor: pointer; }
  .ip-block:hover { background: rgb(var(--md-sys-color-error) / 0.1); color: rgb(var(--md-sys-color-error)); border-color: transparent; }
  .ip-block-icon { font-size: 14px; }
  .ip-blocked { margin-inline-start: 6px; padding: 1px 6px; border-radius: 6px; font-size: 11px; background: rgb(var(--md-sys-color-error) / 0.15); color: rgb(var(--md-sys-color-error)); }
  .kv-section { font-size: 11px; text-transform: uppercase; letter-spacing: 0.05em; color: rgb(var(--md-sys-color-on-surface-variant)); }
  .body-pre { margin: 0; padding: 0.625rem 0.75rem; background: rgb(var(--md-sys-color-surface-container)); border-radius: 8px; font-family: 'Menlo','Consolas',monospace; font-size: 12px; line-height: 1.5; white-space: pre-wrap; word-break: break-all; max-block-size: 480px; overflow: auto; }
</style>
